import { useQueryNotifications } from '@/hooks/query/useQueryNotifications';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import {
  hcpStatusSignalIds,
  hcpStatusSignalToHeal,
} from './hcp-verification-inmail-sync';
import {
  GET_HCP_VERIFICATIONS_QUERY_KEY,
  useQueryHcpVerifications,
} from './use-query-hcp-verifications';

const EMPTY_BELL: ReadonlySet<string> = new Set();

/**
 * Keeps the status cache on the same clock as the bell. Each time the status
 * settles, the grant/refuse ids then in the bell are recorded: those rows were
 * committed before that read and are older than the cache, whatever the
 * cache says. Each notifications fetch (60s / focus) is then a chance to
 * heal: a newer grant or refuse that the status disagrees with invalidates
 * it once. The settle that follows records it, so nothing loops, and a
 * heal already in flight is never doubled.
 *
 * The mark starts at the current `dataUpdatedAt` with an empty bell, so a
 * probe mounting against a status cached earlier checks the bell instead of
 * trusting it — the cache may predate a row that landed while unmounted.
 * That costs at most one read when an old refuse disagrees with a correct
 * pending cache; the settle then records it.
 */
export function useSyncHcpVerificationFromInmails(): void {
  const { data: inmails } = useQueryNotifications();
  const statusQuery = useQueryHcpVerifications();
  const queryClient = useQueryClient();
  const settledRef = useRef<{ at: number; bell: ReadonlySet<string> }>({
    at: statusQuery.dataUpdatedAt,
    bell: EMPTY_BELL,
  });

  const status =
    statusQuery.isEnabled && !statusQuery.isError && !statusQuery.isPending
      ? statusQuery.data
      : undefined;

  useEffect(() => {
    if (status === undefined) return;

    if (settledRef.current.at !== statusQuery.dataUpdatedAt) {
      settledRef.current = {
        at: statusQuery.dataUpdatedAt,
        bell: hcpStatusSignalIds(inmails),
      };
      return;
    }
    if (statusQuery.isFetching) return;

    const signal = hcpStatusSignalToHeal(inmails, status, settledRef.current.bell);
    if (signal === null) return;
    void queryClient.invalidateQueries({ queryKey: [GET_HCP_VERIFICATIONS_QUERY_KEY] });
  }, [inmails, status, statusQuery.dataUpdatedAt, statusQuery.isFetching, queryClient]);
}
